import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Badge,
  Button,
} from "react-bootstrap";
import {
  FaRupeeSign,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
  FaUser,
  FaClock,
  FaSyncAlt,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import api from "../api";
import { AuthContext } from "../context/AuthContext";

const STATUS_VARIANTS = {
  pending: "warning",
  confirmed: "success",
  cancelled: "secondary",
};

const STATUS_LABELS = {
  pending: "Pending",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
};

export default function PurchaseRequests() {
  const { user } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [success, setSuccess] = useState("");
  const [updatingId, setUpdatingId] = useState("");

  const hasSellerProfile = useMemo(() => Boolean(user?.email), [user]);

  const fetchRequests = async () => {
    if (!hasSellerProfile) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/items/purchases", {
        params: { ownerEmail: user.email },
      });
      setRequests(res.data?.purchases || []);
    } catch (err) {
      console.error("Purchase request fetch error:", err);
      setRequests([]);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load purchase requests."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSellerProfile]);

  const updateStatus = async (purchaseId, status) => {
    if (!purchaseId || !status || !user?.email) {
      return;
    }

    setUpdatingId(`${purchaseId}:${status}`);
    setUpdateError("");
    setSuccess("");
    try {
      const res = await api.patch(`/items/purchases/${purchaseId}`, {
        ownerEmail: user.email,
        status,
      });
      if (res.data?.success && res.data?.purchase) {
        setRequests((prev) =>
          prev.map((request) =>
            request._id === purchaseId ? res.data.purchase : request
          )
        );
        setSuccess(`Marked request as ${STATUS_LABELS[status].toLowerCase()}.`);
      } else {
        setUpdateError(
          res.data?.message || "Failed to update the purchase request."
        );
      }
    } catch (err) {
      console.error("Purchase status update error:", err);
      setUpdateError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to update the purchase request."
      );
    } finally {
      setUpdatingId("");
    }
  };

  const renderStatusButtons = (request) => {
    const buttons = [];
    if (request.status !== "confirmed") {
      buttons.push({ status: "confirmed", label: "Confirm", Icon: FaCheck });
    }
    if (request.status !== "cancelled") {
      buttons.push({ status: "cancelled", label: "Cancel", Icon: FaTimes });
    }
    if (request.status !== "pending") {
      buttons.push({ status: "pending", label: "Reset", Icon: FaSyncAlt });
    }

    return buttons.map(({ status, label, Icon }) => {
      const isUpdating = updatingId === `${request._id}:${status}`;
      const isCurrentRequestUpdating = updatingId.startsWith(`${request._id}:`);
      const disableButton = isCurrentRequestUpdating && !isUpdating;

      return (
        <Button
          key={status}
          variant={
            status === "cancelled" ? "outline-secondary" : "outline-primary"
          }
          size="sm"
          className="d-flex align-items-center gap-1"
          disabled={disableButton}
          onClick={() => updateStatus(request._id, status)}
        >
          {isUpdating ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <Icon size={12} />
          )}
          {label}
        </Button>
      );
    });
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    return new Date(value).toLocaleString();
  };

  if (!hasSellerProfile) {
    return (
      <Container className="py-4">
        <Alert variant="info">
          Log in to view purchase requests for your marketplace listings.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h3 className="fw-bold mb-1">Purchase Requests</h3>
          <p className="text-muted mb-0">
            Track and manage purchase inquiries for your sale listings.
          </p>
        </Col>
      </Row>

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {updateError && (
        <Alert variant="danger" dismissible onClose={() => setUpdateError("")}>
          {updateError}
        </Alert>
      )}

      {error && <Alert variant="warning">{error}</Alert>}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : requests.length === 0 ? (
        <Card className="shadow-sm border-0 rounded-4">
          <Card.Body className="text-center text-muted py-5">
            <FaClock size={48} className="mb-3" />
            <div className="fw-semibold">No purchase requests yet.</div>
            <div>
              Sale inquiries will appear here once buyers send purchase
              confirmations.
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-4">
          {requests.map((request) => (
            <Col key={request._id} md={6}>
              <Card className="h-100 shadow border-0 rounded-4">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h5 className="mb-1">{request.item?.name}</h5>
                      <div className="text-muted small d-flex align-items-center gap-2">
                        <FaMapMarkerAlt className="text-primary" />
                        {request.item?.city || ""}
                        {request.item?.city && request.item?.state ? "," : ""}
                        {request.item?.state || ""}
                      </div>
                    </div>
                    <Badge bg={STATUS_VARIANTS[request.status] || "secondary"}>
                      {STATUS_LABELS[request.status] || request.status}
                    </Badge>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 fw-semibold">
                      <FaRupeeSign /> {request.item?.price ?? "—"}
                    </div>
                    <div className="text-muted small">
                      Requested on {formatDateTime(request.createdAt)}
                    </div>
                  </div>

                  <Card className="bg-light border-0 rounded-3 mb-3">
                    <Card.Body className="py-3">
                      <div className="fw-semibold mb-2 d-flex align-items-center gap-2">
                        <FaUser /> Buyer details
                      </div>
                      <div className="small d-flex flex-column gap-1">
                        {request.buyer?.username && (
                          <span>{request.buyer.username}</span>
                        )}
                        {request.buyer?.email && (
                          <span className="d-inline-flex align-items-center gap-2">
                            <FaEnvelope className="text-primary" />
                            {request.buyer.email}
                          </span>
                        )}
                        {(request.contactPhone || request.buyer?.phone) && (
                          <span className="d-inline-flex align-items-center gap-2">
                            <FaPhone className="text-primary" />
                            {request.contactPhone || request.buyer?.phone}
                          </span>
                        )}
                      </div>
                    </Card.Body>
                  </Card>

                  {request.note && (
                    <div className="mb-3">
                      <div className="fw-semibold">Message</div>
                      <div className="text-muted">{request.note}</div>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer className="bg-white border-0 d-flex gap-2 flex-wrap">
                  {renderStatusButtons(request)}
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
}
